import { isUrl } from '../../../validators';
import type { CommonAttributeSettings } from '../common';
import { DEFAULT_ATTRIBUTE_SETTINGS, defineMetadata, createAttributeSetter } from '../common';

import {
    createTemplateAttributeGetter,
    createThemeRef,
    isTemplateReference,
    ThemeRefTypes,
    validateTemplateReference,
} from './common';

export const FONT_ATTRIBUTE_TYPE = 'fontAttributeType';

function fontValueValidator(
    this: { template?: Record<string, unknown[]>; name?: string },
    value: string,
): void {
    if (isTemplateReference(value)) {
        if (!value.includes(ThemeRefTypes.FONTS)) {
            throw Error(`Invalid value for font theme ref: ${value}`);
        }
        validateTemplateReference.call(this, value, ThemeRefTypes.FONTS);
    } else if (!isUrl(value)) {
        throw Error(`Invalid font value: ${value}`);
    }
}

export function templateFontsByIndex(index: number): string {
    return createThemeRef(ThemeRefTypes.FONTS, index);
}

/**
 * Creates an attribute decorator used to describe a font property. This will enforce either a valid url
 * value or a theme reference string such as themeRef_fonts_0.
 */
export function FontAttribute(settings: Partial<CommonAttributeSettings> = {}) {
    settings = { ...DEFAULT_ATTRIBUTE_SETTINGS, ...settings };
    const validator = fontValueValidator;
    return function <This>(
        { get, set }: ClassAccessorDecoratorTarget<This, string>,
        context: ClassAccessorDecoratorContext,
    ): ClassAccessorDecoratorResult<This, string> {
        defineMetadata(settings, FONT_ATTRIBUTE_TYPE, context, get);

        return {
            get: createTemplateAttributeGetter(get, ThemeRefTypes.FONTS) as (this: This) => string,
            set: createAttributeSetter(validator, get, set),
        };
    };
}
